import { Button } from "@msuite/ui/button";
import { NumberInput, Select } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Tooltip } from "@msuite/ui/tooltip";
import { useFormContext, useWatch } from "react-hook-form";
import { useCostCenters } from "@/hooks/use-cost-centers";
import { amountOf, type CostAllocationFormValues, shareOf } from "./cost-allocation-form-values";

/** Props Interface */
interface CostAllocationFormRowProps {
	index: number;
	netAmount: number;
	/** What is left of the net amount over all rows, in cents. */
	remainingCents: number;
	onRemove: () => void;
}

export function CostAllocationFormRow({
	index,
	netAmount,
	remainingCents,
	onRemove,
}: CostAllocationFormRowProps) {
	/** Context */
	const methods = useFormContext<CostAllocationFormValues>();

	/** Hooks */
	const { t } = useTranslation();
	const { costCenters } = useCostCenters();
	const allocations = useWatch({ control: methods.control, name: "allocations" });

	/** Computed */
	const amount = allocations?.[index]?.amount ?? 0;
	const chosenElsewhere = new Set(
		(allocations ?? [])
			.filter((_, other) => other !== index)
			.map((allocation) => allocation.costCenterId),
	);
	// Only active cost centres are offered, and each once; the one this row already holds stays,
	// even if it was deactivated since, so the row still shows what it is booked to.
	const current = allocations?.[index]?.costCenterId;
	const options = costCenters
		.filter((costCenter) => costCenter.isActive || costCenter.id === current)
		.filter((costCenter) => !chosenElsewhere.has(costCenter.id))
		.map((costCenter) => ({
			value: costCenter.id,
			label: `${costCenter.number} – ${costCenter.name}`,
			item: costCenter,
		}));

	/** Functions */
	// Each of the two fields follows the other only on the person's input, so they never update
	// each other back and forth.
	function handleOnAmountChange(value: number) {
		methods.setValue(`allocations.${index}.share`, shareOf(value ?? 0, netAmount));
	}

	function handleOnShareChange(value: number) {
		methods.setValue(`allocations.${index}.amount`, amountOf(value ?? 0, netAmount), {
			shouldDirty: true,
		});
	}

	function handleOnTakeRest() {
		const rest = (Math.round(amount * 100) + remainingCents) / 100;
		methods.setValue(`allocations.${index}.amount`, rest, { shouldDirty: true });
		methods.setValue(`allocations.${index}.share`, shareOf(rest, netAmount));
	}

	/** Render */
	return (
		<div className="h-stack items-end gap-2">
			<div className="flex-1 min-w-0">
				<Select
					control={methods.control}
					name={`allocations.${index}.costCenterId`}
					label={t("cost_centers.cost_center")}
					placeholder={t("cost_centers.select_cost_center")}
					isRequired
					isNullable={false}
					options={options}
					data-cy={`cost-allocation-cost-center-select-${index}`}
				/>
			</div>
			<div className="w-44">
				<NumberInput
					control={methods.control}
					name={`allocations.${index}.amount`}
					label={t("incoming_invoices.amount")}
					variant="currency"
					hideButtons
					isRequired
					onValueChange={handleOnAmountChange}
				/>
			</div>
			<div className="w-28">
				<NumberInput
					control={methods.control}
					name={`allocations.${index}.share`}
					label={t("incoming_invoices.share")}
					variant="decimal"
					formatOptions={{ style: "unit", unit: "percent" }}
					hideButtons
					isRequired={false}
					onValueChange={handleOnShareChange}
				/>
			</div>
			<Tooltip label={t("incoming_invoices.take_rest")}>
				<Button
					size="sm"
					variant="ghost"
					icon={<Icon name="ArrowDownToLine" />}
					onClick={handleOnTakeRest}
					isDisabled={remainingCents === 0}
					data-cy={`cost-allocation-take-rest-${index}`}
				/>
			</Tooltip>
			<Button
				size="sm"
				variant="danger-ghost"
				icon={<Icon name="Trash2" />}
				onClick={onRemove}
				data-cy={`cost-allocation-remove-${index}`}
			/>
		</div>
	);
}
